/**
 * CJAS — Hijri calendar.  Jira CA-29.
 *
 * Uses the tabular (arithmetic) Islamic calendar. That is a deliberate
 * choice, and the honest framing is this:
 *
 *   The calculated date is a DEFAULT, not the truth. The mosque decides the
 *   date by moon sighting, and that decision must be able to shift the whole
 *   calendar. So there is no value in an elaborate astronomical calculation —
 *   whatever it produced would still be overridden by a person. What matters
 *   is that the override is easy, immediate and propagates everywhere.
 *
 * Ramadan mode, Eid and the app's Hijri display all read from here, so a
 * single adjustment moves every surface at once.
 */

const { startOfCalendarDay } = require('./dates');

const MONTHS_NO = ['Muharram','Safar','Rabi al-Awwal','Rabi al-Thani','Jumada al-Ula',
  'Jumada al-Akhira','Rajab','Sha\'ban','Ramadan','Shawwal','Dhu al-Qi\'dah','Dhu al-Hijjah'];

const MONTHS_UR = ['محرم','صفر','ربیع الاول','ربیع الثانی','جمادی الاول','جمادی الثانی',
  'رجب','شعبان','رمضان','شوال','ذوالقعدہ','ذوالحجہ'];

/** Gregorian date -> Julian Day Number (integer, midday-anchored). */
function toJDN(y, m, d) {
  const a = Math.floor((14 - m) / 12);
  const yy = y + 4800 - a;
  const mm = m + 12 * a - 3;
  return d + Math.floor((153 * mm + 2) / 5) + 365 * yy
    + Math.floor(yy / 4) - Math.floor(yy / 100) + Math.floor(yy / 400) - 32045;
}

/**
 * Convert to the tabular Hijri date.
 * `offsetDays` is the mosque's moon-sighting adjustment: -2..+2, applied
 * before conversion so it shifts the whole calendar, not just the display.
 */
function toHijri(date, offsetDays = 0) {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const jdn = toJDN(d.getFullYear(), d.getMonth() + 1, d.getDate()) + Number(offsetDays || 0);
  return { ...jdnToHijri(jdn), adjusted: Number(offsetDays || 0) !== 0 };
}

/** A Julian Day Number → the tabular Hijri date { year, month, day }. The arithmetic toHijri() has always used. */
function jdnToHijri(jdn) {
  const l0 = jdn - 1948440 + 10632;
  const n = Math.floor((l0 - 1) / 10631);
  let l = l0 - 10631 * n + 354;
  const j = Math.floor((10985 - l) / 5316) * Math.floor((50 * l) / 17719)
          + Math.floor(l / 5670) * Math.floor((43 * l) / 15238);
  l = l - Math.floor((30 - j) / 15) * Math.floor((17719 * j) / 50)
        - Math.floor(j / 16) * Math.floor((15238 * j) / 43) + 29;

  const month = Math.floor((24 * l) / 709);
  const day   = l - Math.floor((709 * month) / 24);
  const year  = 30 * n + j - 30;

  return { year, month, day };
}

/** Julian Day Number → Gregorian { y, m, d } (the inverse of toJDN; integer arithmetic, no Date). */
function fromJDN(jdn) {
  const a = jdn + 32044;
  const b = Math.floor((4 * a + 3) / 146097);
  const c = a - Math.floor(146097 * b / 4);
  const d = Math.floor((4 * c + 3) / 1461);
  const e = c - Math.floor(1461 * d / 4);
  const m = Math.floor((5 * e + 2) / 153);
  return { d: e - Math.floor((153 * m + 2) / 5) + 1, m: m + 3 - 12 * Math.floor(m / 10), y: 100 * b + d - 4800 + Math.floor(m / 10) };
}

/**
 * The Gregorian day of a tabular Hijri date, with the mosque's moon-sighting offset (the same `offsetDays` toHijri()
 * takes, so the two agree: fromHijri(toHijri(day, k), k) is that day).  Work hub (2026-10-31): the annual planner's
 * Hijri-anchored items (Eid, Ashura, Mawlid, Ramadan) are placed with this, once, for every year.
 * → 'YYYY-MM-DD', or null for a date no tabular year has (a 30th of a 29-day month is the 1st of the next: refused).
 */
function fromHijri(year, month, day, offsetDays = 0) {
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day) || month < 1 || month > 12 || day < 1 || day > 30) return null;
  // The tabular formula's own inverse, then checked against jdnToHijri (which is what every surface shows).
  let jdn = Math.floor((11 * year + 3) / 30) + 354 * year + 30 * month - Math.floor((month - 1) / 2) + day + 1948440 - 385;
  for (let tries = 0; tries < 4; tries++) {
    const h = jdnToHijri(jdn);
    if (h.year === year && h.month === month && h.day === day) break;
    const diff = (h.year - year) * 354 + (h.month - month) * 29.5 + (h.day - day);
    if (tries === 3 || diff === 0) return null;
    jdn -= Math.sign(diff) * Math.max(1, Math.round(Math.abs(diff)));
  }
  const g = fromJDN(jdn - Number(offsetDays || 0));
  return `${g.y}-${String(g.m).padStart(2, '0')}-${String(g.d).padStart(2, '0')}`;
}

/**
 * The date as each language writes it.
 *
 * CORRECTED 2026-09-10: English got the Norwegian style — "27. Rabi al-Awwal
 * 1448" — because every language that was not Urdu fell into the Norwegian
 * branch. The ordinal dot is NORWEGIAN only (same rule as i18n.formatDate):
 *   no  "27. Rabi al-Awwal 1448"
 *   en  "27 Rabi al-Awwal 1448"
 *   ur  "27 ربیع الاول 1448"  — Urdu month names, Western digits (i18n:
 *       the printed CJAS timetable uses them), no dot.
 * English uses the same transliterated month names as Norwegian. An unknown
 * language is formatted as Norwegian, the master.
 */
const MONTHS_EN = MONTHS_NO;
function format(hijri, lang = 'no') {
  const l = lang === 'en' || lang === 'ur' ? lang : 'no';
  const names = l === 'ur' ? MONTHS_UR : l === 'en' ? MONTHS_EN : MONTHS_NO;
  const name = names[hijri.month - 1] || '';
  return l === 'no'
    ? `${hijri.day}. ${name} ${hijri.year}`
    : `${hijri.day} ${name} ${hijri.year}`;
}

const isRamadan = h => h.month === 9;
const isEidAlFitr = h => h.month === 10 && h.day === 1;
const isEidAlAdha = h => h.month === 12 && h.day === 10;

/**
 * Pick the adjustment in force on a date. Adjustments are stored with an
 * effective-from date, so history is preserved — last year's Ramadan keeps
 * the offset it actually had rather than being rewritten by this year's.
 */
function activeOffset(adjustments, date) {
  const a = activeAdjustment(adjustments, date);
  return a ? Number(a.offset_days) : 0;
}

/**
 * The adjustment row in force on a date (or null) — so a caller can say WHY
 * the date moved, not only by how much. Same selection as activeOffset().
 */
function activeAdjustment(adjustments, date) {
  if (!adjustments || !adjustments.length) return null;

  // HARDENED 2026-09-09. This compared `new Date(a.effective_from) <= date`,
  // so passing a date STRING — the natural thing, since every date in this
  // platform is a bare `YYYY-MM-DD` — coerced the Date to a number and the
  // string to NaN, every comparison was false, and the function returned 0.
  //
  // Zero is not a neutral answer here. It means "no moon-sighting adjustment
  // in force", so the failure mode was the mosque setting the date for Eid
  // and every screen quietly ignoring it. Both sides go through
  // `startOfCalendarDay` now, which reads a bare date as a local calendar
  // date rather than as UTC midnight (see dates.js).
  const on = startOfCalendarDay(date);
  if (!on) return null;

  // Two set for the same day (a correction the same evening): the one set
  // LAST wins — by created_at where the row has it, else by list order.
  const applicable = adjustments
    .map((a, i) => ({ a, i, from: startOfCalendarDay(a.effective_from) }))
    .filter(x => x.from && x.from <= on)
    .sort((x, y) => (y.from - x.from)
      || String(y.a.created_at || '').localeCompare(String(x.a.created_at || ''))
      || (y.i - x.i));
  return applicable.length ? applicable[0].a : null;
}

module.exports = {
  MONTHS_NO, MONTHS_EN, MONTHS_UR, toJDN, fromJDN, jdnToHijri, toHijri, fromHijri, format,
  isRamadan, isEidAlFitr, isEidAlAdha, activeOffset, activeAdjustment,
};
